import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React from 'react';
import {
  Sun, Moon, Cloud, CloudSun, CloudRain, CloudSnow, CloudLightning,
  CloudDrizzle, CloudFog, Droplets, Eye, Pencil, Trash2, Mail
} from 'lucide-react';
import { Produzione } from '../../types';

// Funzione di utilità per garantire che i valori siano sempre numeri
const ensureNumber = (value: any): number => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return value;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

// Mappa le icone meteo OpenWeatherMap su icone lucide
const weatherIconComponents: { [key: string]: React.ComponentType<any> } = {
  '01d': Sun, '01n': Moon, '02d': CloudSun, '02n': Cloud,
  '03d': Cloud, '03n': Cloud, '04d': Cloud, '04n': Cloud,
  '09d': CloudDrizzle, '09n': CloudDrizzle, '10d': CloudRain, '10n': CloudRain,
  '11d': CloudLightning, '11n': CloudLightning, '13d': CloudSnow, '13n': CloudSnow,
  '50d': CloudFog, '50n': CloudFog,
};

const getWeatherIcon = (iconCode: string): React.ReactElement => {
  const IconComponent = weatherIconComponents[iconCode] || CloudSun;
  return React.createElement(IconComponent, {
    size: 16,
    style: { color: '#607d8b', flexShrink: 0 }
  });
};

// Formatter per le date
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Formatter per date/time completo
const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Renderer functions (returning React elements for AG Grid)
export const dateRenderer = (params: ICellRendererParams) => {
  if (!params.value) return '';
  return React.createElement('span', {
    style: {
      background: '#1976d2',
      color: 'white',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: 600
    }
  }, formatDate(params.value));
};

export const kgTotaleRenderer = (params: ICellRendererParams) => {
  const value = ensureNumber(params.value);
  return React.createElement('span', {
    style: {
      background: '#4caf50',
      color: 'white',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: 600
    }
  }, `${value.toFixed(2)} kg`);
};

export const costoRenderer = (params: ICellRendererParams) => {
  const value = ensureNumber(params.value);
  return React.createElement('span', {
    style: {
      background: '#ff9800',
      color: 'white',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: 600
    }
  }, `€${value.toFixed(2)}`);
};

export const meteoRenderer = (params: ICellRendererParams) => {
  const data = params.data as Produzione;
  
  if (!data.temperatura_media) {
    return React.createElement('span', {
      style: { color: '#9e9e9e', fontStyle: 'italic' }
    }, 'N/D');
  }
  
  const elements = [];
  
  // Temperatura principale
  const tempElements = [];
  if (data.icona) {
    tempElements.push(React.createElement('span', {
      title: data.condizione || '',
      key: 'icon'
    }, getWeatherIcon(data.icona)));
  }
  tempElements.push(React.createElement('span', {
    style: { fontWeight: 600, color: '#1976d2' },
    key: 'temp'
  }, `${ensureNumber(data.temperatura_media).toFixed(1)}°C`));
  
  elements.push(React.createElement('div', {
    style: { display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' },
    key: 'temp-container'
  }, tempElements));
  
  // Precipitazioni
  if (data.precipitazioni && ensureNumber(data.precipitazioni) > 0) {
    elements.push(React.createElement('div', {
      style: { color: '#1976d2', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '3px' },
      key: 'rain'
    }, [
      React.createElement(CloudRain, { size: 11, key: 'rain-icon' }),
      `${ensureNumber(data.precipitazioni).toFixed(1)}mm`
    ]));
  }

  // Umidità
  if (data.umidita) {
    elements.push(React.createElement('div', {
      style: { color: '#607d8b', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '3px' },
      key: 'humidity'
    }, [
      React.createElement(Droplets, { size: 11, key: 'humidity-icon' }),
      `${ensureNumber(data.umidita).toFixed(0)}%`
    ]));
  }
  
  return React.createElement('div', {
    style: { fontSize: '12px' }
  }, elements);
};

export const gustiRenderer = (params: ICellRendererParams) => {
  const data = params.data as Produzione;
  const gusti = data.gusti || [];
  
  if (gusti.length === 0) {
    return React.createElement('span', {
      style: { color: '#9e9e9e', fontStyle: 'italic' }
    }, 'Nessun gusto');
  }
  
  const elements = [];
  
  // Conteggio gusti
  elements.push(React.createElement('div', {
    style: { fontWeight: 600, marginBottom: '4px', color: '#1976d2' },
    key: 'count'
  }, `${gusti.length} gusti`));
  
  // Lista gusti (max 2)
  const gustiElements = [];
  gusti.slice(0, 2).forEach((gusto: any, index: number) => {
    gustiElements.push(React.createElement('div', {
      style: { 
        background: '#f5f5f5', 
        padding: '2px 6px', 
        borderRadius: '4px', 
        fontSize: '11px', 
        marginBottom: '2px' 
      },
      key: `gusto-${index}`
    }, `${gusto.gusto_nome}: ${ensureNumber(gusto.quantita_kg).toFixed(1)}kg`));
  });
  
  if (gusti.length > 2) {
    gustiElements.push(React.createElement('div', {
      style: { color: '#666', fontSize: '11px' },
      key: 'more'
    }, `+${gusti.length - 2} altri`));
  }
  
  elements.push(React.createElement('div', { key: 'gusti-list' }, gustiElements));
  
  return React.createElement('div', {
    style: { fontSize: '12px' }
  }, elements);
};

export const emailStatusRenderer = (params: ICellRendererParams) => {
  const data = params.data as Produzione;
  const isInviata = data.inviato_email;
  
  return React.createElement('span', {
    style: {
      padding: '4px 8px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: 600,
      background: isInviata ? '#e8f5e8' : '#ffebee',
      color: isInviata ? '#2e7d32' : '#d32f2f'
    }
  }, isInviata ? '✓ Inviata' : '✗ Non inviata');
};

export const createdAtRenderer = (params: ICellRendererParams) => {
  return React.createElement('span', {
    style: { fontSize: '12px', color: '#666' }
  }, formatDateTime(params.value));
};

export const actionsRenderer = (params: ICellRendererParams) => {
  // Per le azioni useremo un approccio diverso nel componente principale
  const buttons = [
    React.createElement('button', {
      className: 'btn-view',
      title: 'Visualizza dettagli',
      key: 'view'
    }, React.createElement(Eye, { size: 16, style: { pointerEvents: 'none' } })),
    React.createElement('button', {
      className: 'btn-edit',
      title: 'Modifica',
      key: 'edit'
    }, React.createElement(Pencil, { size: 16, style: { pointerEvents: 'none' } })),
    React.createElement('button', {
      className: 'btn-delete',
      title: 'Elimina',
      key: 'delete'
    }, React.createElement(Trash2, { size: 16, style: { pointerEvents: 'none' } })),
    React.createElement('button', {
      className: 'btn-email',
      title: 'Email',
      key: 'email'
    }, React.createElement(Mail, { size: 16, style: { pointerEvents: 'none' } }))
  ];

  return React.createElement('div', {
    className: 'grid-actions-cell',
    'data-row-id': params.data.id
  }, buttons);
};

export const createColumnDefs = (canViewCosts: boolean): ColDef[] => {
  const columns: ColDef[] = [
    {
      headerName: 'Data',
      field: 'data',
      width: 120,
      pinned: 'left',
      cellRenderer: dateRenderer,
      filter: 'agTextColumnFilter',
      filterValueGetter: (params) => {
        if (!params.data || !params.data.data) return '';
        return formatDate(params.data.data);
      },
      comparator: (valueA: string, valueB: string) => {
        return new Date(valueA).getTime() - new Date(valueB).getTime();
      }
    },
    {
      headerName: 'Punto Vendita',
      field: 'punto_vendita_nome',
      width: 150,
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'Operatore',
      field: 'operatore_nome',
      width: 140,
      filter: 'agTextColumnFilter',
      filterValueGetter: (params) => {
        if (!params.data || !params.data.operatore_nome) return '';
        return String(params.data.operatore_nome).trim();
      }
    },
    {
      headerName: 'Kg Totali',
      field: 'kg_totale_giornaliero',
      width: 120,
      type: 'numericColumn',
      cellRenderer: kgTotaleRenderer,
      filter: 'agNumberColumnFilter'
    },
    {
      headerName: 'Meteo',
      field: 'temperatura_media',
      width: 140,
      cellRenderer: meteoRenderer,
      filter: 'agNumberColumnFilter',
      sortable: true
    },
    ...(canViewCosts ? [{
      headerName: 'Costo Totale',
      field: 'costo_totale',
      width: 130,
      type: 'numericColumn' as const,
      cellRenderer: costoRenderer,
      filter: 'agNumberColumnFilter'
    }] : []),
    {
      headerName: 'Gusti',
      field: 'gusti',
      width: 200,
      cellRenderer: gustiRenderer,
      sortable: false,
      filter: false
    },
    {
      headerName: 'Email',
      field: 'inviato_email',
      width: 100,
      cellRenderer: emailStatusRenderer,
      filter: 'agTextColumnFilter', // Usa filtro testo invece di Set (Community)
      filterParams: {
        textFormatter: (value: any) => value ? 'Inviata' : 'Non inviata'
      }
    },
    {
      headerName: 'Inserito',
      field: 'created_at',
      width: 140,
      cellRenderer: createdAtRenderer,
      filter: 'agTextColumnFilter',
      filterValueGetter: (params) => {
        if (!params.data || !params.data.created_at) return '';
        return formatDateTime(params.data.created_at);
      },
      comparator: (valueA: string, valueB: string) => {
        return new Date(valueA).getTime() - new Date(valueB).getTime();
      }
    },
    {
      headerName: 'Azioni',
      field: 'actions',
      width: 150,
      cellRenderer: actionsRenderer,
      sortable: false,
      filter: false,
      pinned: 'right'
    }
  ];

  return columns;
};
